import classnames from 'classnames';
import React from 'react';

import Icon from 'dpl/common/components/Icon';
import SmartImage from 'dpl/components/SmartImage';
import { PAYMENT_REQUEST_CATEGORIES } from 'dpl/shared/constants/breeder_app';
import { numberWithoutZeroDecimal } from 'dpl/shared/utils/currency';

import { getDepositRefundPolicyAdjective } from './util';

interface StripeChargeModalPuppyCardV3Props {
  className?: string | null;
  dog?: {
    name: string;
    profile_photo_url?: string | null;
    breed_name?: string | null;
    breed?: { name?: string | null } | null;
  } | null;
  litter?: {
    id?: number | null;
    name?: string | null;
    // "Sire x Dam's litter", or "Golden Retriever litter" without parents
    litter_title?: string | null;
    deposit_refund_policy?: string | null;
  } | null;
  // the total due, including fees
  amountInDollars?: number | null;
  amountLabel?: string;
  paymentCategory?: string;
  // the full /api/breeder_profiles/:id payload once it loads; the breeder
  // block waits for display_name
  breederProfile: {
    name: string;
    display_name?: string | null;
    city_state?: string | null;
    avatar_profile_photo_url?: string | null;
    percent_recommend?: number | null;
    testimonials_count?: number | null;
    deposit_refund_policy?: string | null;
    deposit_secures?: string | null;
  };
  dogDescription?: string | null;
  onEditClick?: (() => void) | null;
  isEditDisabled?: boolean;
  onAmountClick?: (() => void) | null;
  onTermsInfoClick?: (() => void) | null;
  notice?: React.ReactNode;
}

interface BreederRecommendationProps {
  percentRecommend?: number | null;
  testimonialsCount?: number | null;
}

function BreederRecommendation({
  percentRecommend = null,
  testimonialsCount = null
}: BreederRecommendationProps) {
  if (percentRecommend == null) return null;

  return (
    <p className="flex items-center gap-4 font-12 secondary">
      <Icon
        className="green-dark-700"
        name="fetch-verified"
        width="16px"
        height="16px"
      />
      <span>
        <span className="fw-medium">{percentRecommend}% recommend</span>
        {testimonialsCount != null &&
          testimonialsCount > 0 &&
          ` • ${testimonialsCount} owner ${
            testimonialsCount === 1 ? 'story' : 'stories'
          }`}
      </span>
    </p>
  );
}

interface AmountPillProps {
  value: string;
  onClick?: (() => void) | null;
}

function AmountPill({ value, onClick = null }: AmountPillProps) {
  return (
    <button
      type="button"
      className="AmountPill inline-flex items-center gap-6 br-pill bg-stone-700 font-14 lh-copy white pv1 ph3"
      onClick={onClick ?? undefined}
      disabled={!onClick}
      data-test-id="checkout-amount-pill"
    >
      <span>{value}</span>
      <Icon name="fetch-chevron-down" height="12px" width="12px" />
    </button>
  );
}

export default function StripeChargeModalPuppyCardV3({
  className = null,
  dog = null,
  litter = null,
  amountInDollars = null,
  amountLabel = 'Amount due',
  paymentCategory = PAYMENT_REQUEST_CATEGORIES.DOG_PAYMENT,
  breederProfile,
  dogDescription = null,
  onEditClick = null,
  isEditDisabled = false,
  onAmountClick = null,
  onTermsInfoClick = null,
  notice = null
}: StripeChargeModalPuppyCardV3Props) {
  const photoSize = '64px';
  const totalWithDollar = numberWithoutZeroDecimal(amountInDollars);
  const breedName = dog?.breed?.name || dog?.breed_name;
  const isDeposit = paymentCategory === PAYMENT_REQUEST_CATEGORIES.DEPOSIT;
  const isDogPayment =
    paymentCategory === PAYMENT_REQUEST_CATEGORIES.DOG_PAYMENT;
  const titleText =
    isDeposit || isDogPayment
      ? `${isDeposit ? 'Deposit' : 'Payment'} for ${dog?.name ?? 'a puppy'}`
      : `Payment to ${breederProfile.name}`;

  // deposit_refund_policy is force-synced to active litters, so litter vs
  // program is moot there.
  const depositRefundPolicy =
    litter?.deposit_refund_policy || breederProfile.deposit_refund_policy;

  // deposit_secures is program-level only: the litter column is
  // orphaned/nonexistent, so it never reads from litter.
  const depositSecures = breederProfile.deposit_secures ?? null;

  const breederSubtitle = [breederProfile.name, breederProfile.city_state]
    .filter(Boolean)
    .join(', ');

  function renderSubtitle() {
    // a deposit or final payment names its litter even once a puppy is picked
    if ((isDeposit || isDogPayment) && litter?.litter_title) {
      return `from ${litter.litter_title}`;
    }
    if (isDogPayment && dogDescription) return dogDescription;
    if (isDogPayment && breedName) return `${breedName} puppy`;
    if (!dog && litter?.name) return `From ${litter.name}'s litter`;

    return null;
  }

  // The sentence says what this payment reserves, whatever the breeder's
  // deposit_secures says.
  function getPaymentSecuresText() {
    if (dog?.name) return dog.name;
    // the BIP checkout holds an empty litter until its payment request
    // exists, so this keys on the id
    if (litter?.id) return 'your spot in the litter';

    return 'your spot on the waitlist';
  }

  // The breeder's deposit_secures choice is a tap away, in the Breeder's note
  // behind the info icon.
  function renderTermsSentence() {
    if (!isDeposit) return null;

    const refundPolicyAdjective =
      getDepositRefundPolicyAdjective(depositRefundPolicy);

    if (!depositSecures && !refundPolicyAdjective) return null;

    return `Your ${
      refundPolicyAdjective ? `${refundPolicyAdjective} ` : ''
    }deposit reserves ${getPaymentSecuresText()}.`;
  }

  const subtitle = renderSubtitle();
  const termsSentence = renderTermsSentence();

  return (
    <div
      className={classnames(
        'StripeChargeModalPuppyCardV3 bg-sand br12 ba b--gray-300 ph4 pv4',
        className
      )}
      data-test-id="checkout-puppy-card"
    >
      <div className="flex items-center gap-16 pb4 bb b--gray-300">
        <div className="flex-none">
          {dog?.profile_photo_url ? (
            <SmartImage
              src={dog.profile_photo_url}
              width={photoSize}
              height={photoSize}
              className="br3 bg-white"
              loadingClass="bg-near-white"
              crop
              lazy
            />
          ) : (
            <div
              className="flex items-center justify-center br3 ba b--stone-700-20 bg-blue-100"
              style={{ width: photoSize, height: photoSize }}
            >
              <Icon
                className="stone-700"
                name="fetch-dog"
                width="28px"
                height="28px"
              />
            </div>
          )}
        </div>
        <div className="flex-auto">
          <p className="font-serif font-18 lh-title">{titleText}</p>
          {subtitle && <p className="font-14 secondary">{subtitle}</p>}
        </div>
        {onEditClick && (
          <button
            type="button"
            className="flex-none self-start bg-white pv2 ph2 br-100"
            onClick={onEditClick}
            disabled={isEditDisabled}
            aria-label="Edit payment"
            data-test-id="checkout-edit-payment"
          >
            <Icon
              name="fetch-pencil"
              className="stone-700"
              width="20px"
              height="20px"
            />
          </button>
        )}
      </div>

      {breederProfile.display_name && (
        <div className="flex items-center gap-12 pv4 bb b--gray-300">
          <div className="flex-auto">
            <p className="font-14 fw-medium">
              Raised by {breederProfile.display_name}
            </p>
            {breederSubtitle && (
              <p className="font-12 secondary">{breederSubtitle}</p>
            )}
            <BreederRecommendation
              percentRecommend={breederProfile.percent_recommend}
              testimonialsCount={breederProfile.testimonials_count}
            />
          </div>
          {breederProfile.avatar_profile_photo_url && (
            <SmartImage
              src={breederProfile.avatar_profile_photo_url}
              width="32px"
              height="32px"
              className="br-100 flex-none"
              crop
              lazy
            />
          )}
        </div>
      )}

      <div className="flex items-center gap-8 pt4">
        <Icon name="gooddogavatar" width="20px" height="20px" />
        <p className="flex-auto font-14">{amountLabel}</p>
        {amountInDollars != null && (
          <AmountPill value={totalWithDollar} onClick={onAmountClick} />
        )}
      </div>

      {termsSentence && (
        <p className="font-14 secondary mt4">
          {termsSentence}
          {onTermsInfoClick && (
            <button
              type="button"
              className="v-mid ml1"
              onClick={onTermsInfoClick}
              aria-label="Breeder’s note"
              data-test-id="checkout-terms-info"
            >
              <Icon name="fetch-info" width="16px" height="16px" />
            </button>
          )}
        </p>
      )}

      {notice && <div className="mt4">{notice}</div>}
    </div>
  );
}
